import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import { attendanceAPI, branchAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatusPill } from '@/components/StatusPill';
import { KpiCard } from '@/components/KpiCard';
import { toast } from 'sonner';
import { Users, Clock, AlertTriangle, CheckCircle } from 'lucide-react';

const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function AttendanceViewPage() {
  const { user, isSuperUser, isManager, isStaff } = useAuth();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [todayData, setTodayData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBranches();
  }, []);

  useEffect(() => {
    if (selectedBranch) {
      loadTodayAttendance();
      loadMonthlyAttendance();
    }
  }, [selectedBranch, selectedYear, selectedMonth]);

  const loadBranches = async () => {
    try {
      const res = await branchAPI.list();
      setBranches(res.data);
      if (res.data.length > 0) {
        const defaultBranch = user?.branch_id || res.data[0].id;
        setSelectedBranch(defaultBranch);
      }
    } catch {
      toast.error('Failed to load branches');
    } finally {
      setLoading(false);
    }
  };

  const loadTodayAttendance = async () => {
    try {
      const res = await attendanceAPI.today(selectedBranch);
      setTodayData(res.data);
    } catch (e) { console.error(e); }
  };

  const loadMonthlyAttendance = async () => {
    try {
      const params = { branch_id: selectedBranch, year: selectedYear, month: selectedMonth };
      if (isStaff) params.employee_id = user?.user_id;
      const res = await attendanceAPI.monthly(params);
      setMonthlyData(res.data);
    } catch (e) { console.error(e); }
  };

  const getAttendanceStatus = (record) => {
    if (record.is_half_day) return 'half-day';
    if (record.is_late) return 'late';
    return 'on-time';
  };

  if (loading) {
    return <div className="h-32 bg-muted animate-pulse rounded-xl" />;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">Attendance</h1>
          <p className="text-muted-foreground">Track team attendance and punctuality</p>
        </div>
        {(isSuperUser || isManager) && branches.length > 0 && (
          <Select value={selectedBranch} onValueChange={setSelectedBranch} data-testid="branch-switcher-select">
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Select branch" />
            </SelectTrigger>
            <SelectContent>
              {branches.map(b => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <Tabs defaultValue="today" className="w-full">
        <TabsList>
          <TabsTrigger value="today" data-testid="attendance-today-tab">Today</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="attendance-monthly-tab">Monthly</TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="space-y-4 mt-4">
          {todayData && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard title="Present" value={todayData.total_present} icon={CheckCircle} />
                <KpiCard title="Late" value={todayData.total_late} icon={Clock} />
                <KpiCard title="Half Day" value={todayData.total_half_day} icon={AlertTriangle} />
                <KpiCard title="Absent" value={todayData.total_absent} icon={Users} />
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-heading">Today's Attendance - {todayData.date}</CardTitle>
                </CardHeader>
                <CardContent>
                  {todayData.records.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No check-ins yet today</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Employee</TableHead>
                            <TableHead>Check-in</TableHead>
                            <TableHead>Check-out</TableHead>
                            <TableHead className="text-right">Late (min)</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>GPS</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {todayData.records.map(record => (
                            <TableRow key={record.id}>
                              <TableCell className="font-medium">{record.employee_name}</TableCell>
                              <TableCell className="tabular-nums">{record.check_in_display || record.check_in_time_local}</TableCell>
                              <TableCell className="tabular-nums">
                                {record.check_out_display || '-'}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">
                                {record.late_minutes > 0 ? record.late_minutes : '-'}
                              </TableCell>
                              <TableCell>
                                <StatusPill status={getAttendanceStatus(record)} />
                              </TableCell>
                              <TableCell>
                                {record.gps_latitude ? (
                                  <span className="text-xs text-green-600">Active</span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">N/A</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {todayData.absent_employees?.length > 0 && (
                    <div className="mt-6 pt-4 border-t">
                      <p className="text-sm font-medium text-muted-foreground mb-3">Absent Today</p>
                      <div className="flex flex-wrap gap-2">
                        {todayData.absent_employees.map(emp => (
                          <StatusPill key={emp.id} status="absent">{emp.name}</StatusPill>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="monthly" className="space-y-4 mt-4">
          <div className="flex gap-3">
            <Select value={String(selectedMonth)} onValueChange={v => setSelectedMonth(Number(v))}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((m, i) => (
                  <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(selectedYear)} onValueChange={v => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2026, 2027, 2028].map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {monthlyData && (
            <>
              {monthlyData.summary?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg font-heading">Summary - {months[selectedMonth - 1]} {selectedYear}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Employee</TableHead>
                          <TableHead className="text-right">Days Present</TableHead>
                          <TableHead className="text-right">Lates</TableHead>
                          <TableHead className="text-right">Half Days</TableHead>
                          <TableHead className="text-right">Total Late Min</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {monthlyData.summary.map(s => (
                          <TableRow key={s.employee_id}>
                            <TableCell className="font-medium">{s.employee_name}</TableCell>
                            <TableCell className="text-right tabular-nums">{s.days_present}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              <span className={s.total_lates > 0 ? 'text-amber-600 font-medium' : ''}>{s.total_lates}</span>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              <span className={s.total_half_days > 0 ? 'text-blue-600 font-medium' : ''}>{s.total_half_days}</span>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">{s.total_late_minutes}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-heading">Detailed Records</CardTitle>
                </CardHeader>
                <CardContent>
                  {monthlyData.records?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No records for this period</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Employee</TableHead>
                            <TableHead>Check-in</TableHead>
                            <TableHead>Check-out</TableHead>
                            <TableHead className="text-right">Late (min)</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {monthlyData.records.map(r => (
                            <TableRow key={r.id}>
                              <TableCell className="tabular-nums">{r.date}</TableCell>
                              <TableCell className="font-medium">{r.employee_name}</TableCell>
                              <TableCell className="tabular-nums">{r.check_in_display || r.check_in_time_local}</TableCell>
                              <TableCell className="tabular-nums">{r.check_out_display || '-'}</TableCell>
                              <TableCell className="text-right tabular-nums">{r.late_minutes > 0 ? r.late_minutes : '-'}</TableCell>
                              <TableCell><StatusPill status={getAttendanceStatus(r)} /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
